import { Meta } from '@angular/platform-browser'
import { Injector, NgModule, PLATFORM_ID } from '@angular/core'
import { translationChunksConfig, translations } from '@spartacus/assets'
import { Config, HttpErrorHandler, I18nConfig, OccConfig, provideConfig, provideConfigFactory, } from '@spartacus/core'
import { SharedModule } from './shared/shared.module'
import { ProductModule } from './product/product.module'
import { CartModule } from './cart/cart.module'
import { PAGE_LAYOUT_HANDLER, } from '@spartacus/storefront'
import { MbCartPageLayoutHandler } from './cart/mb-cart-page-layout.handler'
import { customLayoutConfig } from '../../custom-layout-config'
import { CmsModule } from './cms/cms.module'
import { SiteContextModule } from './site-context/site-context.module'
import { StoreContextModule } from './store-context/store-context.module'
import { SearchModule } from './search/search.module'
import { MbSearchPageLayoutHandler } from './search/mb-search-page-layout.handler'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { CheckoutModule } from './checkout/checkout.module'
import { UserModule } from './user/user.module'
import { MbBadRequestHandler } from './global-message/http-interceptors/handlers/bad-request/mb-bad-request.handler'
import { HomepageModule } from './homepage/homepage.module'
import { MyAccountModule } from './myaccount/my-account.module'
import { AuthModule } from './auth/auth.module'
import { environment } from '../../environments/environment'
import { ScriptsModule } from './scripts/scripts.module'
import { AnalyticsModule } from './analytics/analytics.module'
import { isPlatformServer } from '@angular/common'
import { REQUEST } from '@nguniversal/express-engine/tokens'
import { PoUploadModule } from './po-upload/po-upload.module'
import { JobSearchRootModule } from './job-search/job-search-root.module'
import {
  MbInternalServerErrorHandler
} from './global-message/http-interceptors/handlers/internal-server/mb-internal-server.handler'
import { MpupModule } from './mpup/mpup.module'
import { MiltenyiWishlistModule } from './wishlist/wishlist.module'
import { CartPageLayoutHandler } from '@spartacus/cart/base/components'
import { UniversityRootModule } from './university/university-root.module'

const { occ, proxies } = environment || {}

@NgModule({
  imports: [
    BrowserAnimationsModule,
    SiteContextModule,
    StoreContextModule,
    SharedModule,
    AuthModule,
    HomepageModule,
    ProductModule,
    SearchModule,
    UserModule,
    CartModule,
    CheckoutModule,
    MyAccountModule,
    UniversityRootModule,
    ScriptsModule,
    AnalyticsModule,
    CmsModule,
    PoUploadModule,
    JobSearchRootModule,
    MpupModule,
    MiltenyiWishlistModule
  ],
  providers: [
    {
      provide: CartPageLayoutHandler,
      useExisting: MbCartPageLayoutHandler,
    },
    {
      provide: PAGE_LAYOUT_HANDLER,
      useExisting: MbSearchPageLayoutHandler,
      multi: true,
    },
    {
      provide: HttpErrorHandler,
      useExisting: MbBadRequestHandler,
      multi: true
    },
    {
      provide: HttpErrorHandler,
      useExisting: MbInternalServerErrorHandler,
      multi: true
    },
    provideConfigFactory((injector: Injector) => {
      // will go through proxy. for SSR we need the path, for CSR is constructed by the browser
      let baseUrl = occ.baseUrl
      if (proxies && proxies.length && proxies.find((p: any) => !p.disabled && p.target === baseUrl)) {
        const platformId = injector.get(PLATFORM_ID)
        const meta = injector.get(Meta)
        const hasSSR = meta.getTag('name=ssr') // is this an SSR response?
        if (hasSSR?.content === 'true') {
          baseUrl = '' // set base path to empty so that the browser appends the domain and goes through proxy
        }
        if (isPlatformServer(platformId)) {
          const request = injector.get(REQUEST)
          const protocol = request.headers['x-forwarded-proto'] || request.protocol
          const host = request.headers['x-forwarded-host'] || request.headers['host'] || `${request.hostname}:${request.socket.localPort
          || (request.socket.address() as any).port}`
          baseUrl = `${protocol}://${host}`
          meta.updateTag({ name: 'ssr', content: 'true' })
        }
      }
      return {
        backend: {
          occ: {
            baseUrl
          }
        }
      } as OccConfig
    }, [Injector]),
    provideConfig({
      backend: {
        occ: {
          prefix: occ.prefix || '/occ/v2',
          endpoints: {
            baseSites:
              'basesites?fields=baseSites(uid,fallback,supportingAccountCreation,supportingGuestCheckout,maxAllowedQuantityPerProductInCart,country(FULL),' +
              'defaultLanguage(isocode),urlEncodingAttributes,urlPatterns,allSites,currency(FULL),stores(currencies(isocode),defaultCurrency(isocode),languages(isocode),' +
              'defaultLanguage(isocode),deliveryCountries),' +
              'theme,defaultPreviewCatalogId,defaultPreviewCategoryCode,' +
              'defaultPreviewProductCode)',
            user: 'users/${userId}',
            addEntries:
              'users/${userId}/carts/${cartId}/entries?qty=${quantity}',
            product: {
              details:
                'products/byName/${urlEncodedName}' +
                '?fields=averageRating,stock(DEFAULT),description,availableForPickup,code,url,price(DEFAULT),' +
                'numberOfReviews,manufacturer,categories(FULL),priceRange,multidimensional,tags,images(FULL),' +
                'applicationImages,clone,size,regulatoryStatus,clinicalReferenceNumber,userManuals,dataSheets,filters(FULL),urlEncodedName,seoTitle,seoDescription,isAntibody',
              variants:
                'products/byName/${urlEncodedName}' +
                '?fields=name,categories,purchasable,baseOptions(DEFAULT),baseProduct,variantOptions(DEFAULT),variantType,filters(FULL),productLine(FULL),urlEncodedName,isAntibody',
              list: 'products/byName/${urlEncodedName}?fields=code,name,summary,price(formattedValue),images(DEFAULT,galleryIndex),baseProduct,urlEncodedName',
              attributes: 'products/byName/${urlEncodedName}?fields=classifications',
              configurator:
                'products/byName/${urlEncodedName}?fields=code,configurable,configuratorType',
              configuratorProductCard:
                'products/byName/${urlEncodedName}?fields=code,description,images(DEFAULT)',
              default:
                'products/byName/${urlEncodedName}' +
                '?fields=DEFAULT,averageRating,images(FULL),classifications,manufacturer,numberOfReviews,' +
                'categories(FULL),baseOptions,baseProduct,variantOptions,variantType,applicationImages,clone,size,' +
                'regulatoryStatus,filters(FULL),urlEncodedName,seoTitle,seoDescription',
            },
            productByOriginalCode: 'products/${productCode}?fields=name,description,code,url,price(FULL),images(DEFAULT),' +
              'customerPrice(FULL),priceOnRequest,summary,configurable,variantOptions(FULL)',
            productSearch:
              'products/search?fields=products(code,name,summary,configurable,configuratorType,price(FULL),' +
              'customerPrice(FULL),priceOnRequest,images(DEFAULT),stock(FULL),averageRating,variantOptions(FULL),url,regulatoryStatus,searchInformationMap,categoryNamePath,lastCategoryName),'
              +
              'facets(DEFAULT),breadcrumbs,pagination(DEFAULT),sorts(DEFAULT),freeTextSearch,currentQuery,keywordRedirectUrl',
            productCategoriesSearchMinimal: 'products/cache/search?fields=products(categories),currentQuery(query)',
            productSearchMinimal: 'products/search?fields=products(code,url,variantOptions(code,url)),currentQuery(query)',
            navigationCategories: 'navigation/search',
            cart:
              'users/${userId}/carts/${cartId}?fields=FULL',
            carts:
              'users/${userId}/carts?fields=carts(FULL)',
            createCart:
              'users/${userId}/carts?fields=FULL',
            addresses: 'users/${userId}/addresses?fields=FULL',
            productTabReferences: '/products/${productCode}/tab/references',
            productTabExtendedValidation: '/products/${productCode}/tab/extendedValidation',
            productTabApplications: '/products/${productCode}/tab/applications',
            productTabSpecifications: '/products/${productCode}/tab/specifications',
            productTabProductDataImages: '/products/${productCode}/tab/dataimagefigures',
            productTabResources: '/products/${productCode}/tab/resources',
            academicDegrees: '/academic-degrees',
            jobTitles: '/job-titles',
            titles: '/titles',
            checkoutContactDetails: '/checkout/contact/carts/${cartId}',
            paymentModes: '/checkout/paymentmodes/carts/${cartId}',
            checkoutBillingAddresses: '/users/${userId}/addresses/billing',
            addressBookAddresses: '/users/current/addressbook',
            checkoutBillingForm: '/users/${userId}/carts/${cartId}/addresses/billing',
            checkoutReviewNotes: '/checkout/review/carts/${cartId}',
            checkoutReviewPrices: 'checkout/review/carts/${cartId}/simulate?fields=FULL',
            createDeliveryAddress: 'users/${userId}/carts/${cartId}/addresses/delivery',
            setDeliveryAddress: 'users/${userId}/carts/${cartId}/addresses/delivery',
            checkoutQuoteInfo: '/checkout/quote-info/carts/${cartId}',
            countriesWithNoAccount: '/countries/without-account-creation',
            cartMigration: '/users/${userId}/cart-migration',
            productDetailByCode: '/products/short/${productCode}?client_id=fsclient-espirit&client_secret=EspiritS3cret!&username=cmsmanager-espirit&password=pm',
            homepageFacets: '/products/search?fields=facets(DEFAULT)',
            logout: '/users/current/logout',
            poUpload: '/purchase-order',
            hostedCheckoutPaymentPage: '/users/${userId}/orders/hostedcheckout?fields=FULL',
            placeOrderHostedCheckout: '/users/${userId}/orders/hostedcheckout/return?fields=FULL',
            allProductSearch: '/products/cache/search?fields=FULL',
            resetIngenicoPayment: '/users/${userId}/carts/${cartId}/resetIngenicoPayment',
            wishlist: '/users/${userId}/wishlist?fields=FULL',
            updateWishlist: '/users/${userId}/wishlist/${productCode}?fields=FULL',
            recommendedProducts: '/products/recommended'
          },
        },
      },
      pwa: {
        enabled: false,
      },
      pageMeta: {
        resolvers: [
          {
            property: 'canonicalUrl',
            method: 'resolveCanonicalUrl',
            disabledInCsr: false,
          },
        ],
        canonicalUrl: {
          forceTrailingSlash: false
        }
      },
    } as OccConfig),
    provideConfig({
      i18n: {
        resources: translations,
        chunks: translationChunksConfig,
        fallbackLang: 'en',
      },
    } as I18nConfig),
    provideConfig({
      ...customLayoutConfig,
    } as Config)
  ]
})
export class MbModule {
}
